import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { ModalHeader, ModalContent } from '@creditornot/cb-modal';
import React, { useCallback, useState } from 'react';
import { Form, Field } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { RFFFieldErrorWrapper, WoltModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import validateYupSchema from 'common/utils/validateYupSchema';
import { VatIcon } from 'modules/menu/bulk-actions/components/BulkVatChange';
import { Footer } from 'modules/menu/bulk-actions/components/CreateSpecials/styled';
import {
  VatCategories,
  VatFieldValue,
  initialVatFieldValue,
  useVatCategories,
} from 'modules/menu/components/VatCategories';
import { useBulkChangeVatTracking } from 'modules/menu/hooks/telemetryHooks';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { BatchUpdateVatPayload, OfferWithSelectionAndAuthor, OfferingApiError } from 'modules/shared-menu/types';

import ActionButton from './ActionButton';
import BulkActionErrorList from './BulkActionErrorList';
import { BulkActionModalTitle } from './BulkActionModalTitle';
import { buildBatchVatChangeOfferPayloads } from './transformers';
import { useBatchUpdateOffers } from './useBatchUpdateOffers';
import { updateLocalSelectionData } from './utils';
import { validationSchema } from '../../OfferDetailsForm';

type BulkChangeVatProps = {
  selectedIds: Set<string>;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  padding: 0;
  overflow-y: visible;
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const vatValidationSchema = yup.object().shape({
  vat: validationSchema.fields.vat,
});

export const BulkChangeVat = ({ selectedIds }: BulkChangeVatProps) => {
  const intl = useIntl();
  const [isChangeVatModalOpen, setIsChangeVatModalOpen] = useState(false);
  const { updateSelectionData, selection, offers } = useSelectionData();
  const { data: user } = useCurrentUser();
  const trackChangeVat = useBulkChangeVatTracking([...selectedIds]);
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { formatPluralMessage } = usePluralization();
  const { executeBatchUpdateOffers, isLoading } = useBatchUpdateOffers();

  const vatCategories = useVatCategories();

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => {
      toast.success(
        formatPluralMessage({
          id: 'shared-menu.bulk-actions.toasts.update.success-description',
          count: successfullyUpdatedOffers.length,
        }),
      );
      updateLocalSelectionData(successfullyUpdatedOffers, updateSelectionData);
    },
    [formatPluralMessage, updateSelectionData],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          allOffers={offers}
          errors={errors}
          selectionPrimaryLanguage={selection.primary_language}
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const onSubmit = useCallback(
    async (values: { vat: VatFieldValue }) => {
      const { vat: validVatValues } = vatValidationSchema.validateSync(values);
      const payload: BatchUpdateVatPayload = {
        vat_category_code: validVatValues.categoryCode,
        vat_percentage: Number(validVatValues.percentage),
      };

      const offersToUpdate = offers.filter(offer => selectedIds.has(offer.id));

      const offerPayloads = buildBatchVatChangeOfferPayloads(
        payload,
        offersToUpdate,
        selection.id,
        user?._id.$oid ?? '',
      );

      await executeBatchUpdateOffers({
        offerPayloads,
        onSuccess,
        onError,
      });

      trackChangeVat(Number(payload.vat_percentage));

      setIsChangeVatModalOpen(false);
    },
    [executeBatchUpdateOffers, offers, onError, onSuccess, selectedIds, selection.id, trackChangeVat, user?._id.$oid],
  );

  const onClose = useCallback(() => setIsChangeVatModalOpen(false), []);

  return (
    <>
      {menuPermissions.itemVatChange && (
        <ActionButton
          disabled={!menuPermissions.contentEdit || !menuPermissions.itemVatChange}
          icon={<VatIcon />}
          onClick={() => setIsChangeVatModalOpen(true)}
          tooltipTitle={<FormattedMessage id="menu.bulkActions.vatChange.title" />}
        />
      )}

      {isChangeVatModalOpen && (
        <Root isOpen onRequestClose={onClose}>
          <ModalHeader onCloseClick={onClose}>
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="menu.bulkActions.vatChange.title" />}
            />
          </ModalHeader>
          <Form<{ vat: VatFieldValue }>
            initialValues={{ vat: initialVatFieldValue(vatCategories.data?.list, undefined) }}
            onSubmit={onSubmit}
            validate={validateYupSchema(vatValidationSchema)}
            keepDirtyOnReinitialize
          >
            {({ handleSubmit, pristine, hasValidationErrors }) => (
              <form onSubmit={handleSubmit}>
                <ModalContent style={{ padding: 0 }}>
                  <FormRow
                    horizontalPadding
                    label={intl.formatMessage({ id: 'menu-item.vat-label-edit' })}
                    labelAlign="center"
                  >
                    <Field<VatFieldValue> name="vat">
                      {field => (
                        <RFFFieldErrorWrapper field={field}>
                          {input => (
                            <VatCategories
                              {...input}
                              vatCategories={vatCategories.data?.list || []}
                              disabled={isLoading || !vatCategories.data}
                              lang={intl.locale}
                            />
                          )}
                        </RFFFieldErrorWrapper>
                      )}
                    </Field>
                  </FormRow>
                </ModalContent>
                <Footer>
                  <CancelButton onClick={onClose}>
                    <FormattedMessage id="state.cancel" />
                  </CancelButton>
                  <SubmitButton
                    disabled={pristine || hasValidationErrors || isLoading || !vatCategories.data}
                    loading={isLoading || vatCategories.isLoading}
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <FormattedMessage id="state.save" />
                  </SubmitButton>
                </Footer>
              </form>
            )}
          </Form>
        </Root>
      )}
    </>
  );
};
